/**
 * VelocityChart - Sprint velocity visualization
 */

import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { analyticsApi, type VelocityData } from '@services/api';

interface VelocityChartProps {
  projectId: string;
  sprintCount?: number;
}

export default function VelocityChart({ projectId, sprintCount = 6 }: VelocityChartProps) {
  const { t } = useTranslation();
  const [data, setData] = useState<VelocityData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const velocityData = await analyticsApi.getVelocity(projectId, sprintCount);
        setData(velocityData);
      } catch (err) {
        setError(t('analytics.velocityError'));
      } finally {
        setIsLoading(false);
      }
    };

    void fetchData();
  }, [projectId, sprintCount, t]);

  const { maxValue, averageVelocity } = useMemo(() => {
    if (data.length === 0) return { maxValue: 0, averageVelocity: 0 };

    const max = Math.max(...data.map(d => Math.max(d.plannedPoints, d.completedPoints)));
    const avg = data.reduce((sum, d) => sum + d.velocity, 0) / data.length;

    return { maxValue: max || 1, averageVelocity: avg };
  }, [data]);

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
        <div className="text-center text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
            {t('analytics.velocity')}
          </h3>
          <p className="text-sm text-neutral-500">
            {t('analytics.velocityDescription')}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-primary-500 rounded-sm" />
            <span className="text-xs text-neutral-500">{t('analytics.planned')}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-green-500 rounded-sm" />
            <span className="text-xs text-neutral-500">{t('analytics.completed')}</span>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="relative h-64">
        {/* Y-axis labels */}
        <div className="absolute left-0 top-0 bottom-0 w-12 flex flex-col justify-between text-xs text-neutral-500">
          <span>{maxValue}</span>
          <span>{Math.round(maxValue / 2)}</span>
          <span>0</span>
        </div>

        {/* Chart area */}
        <div className="ml-14 h-full flex items-end justify-around gap-4">
          {data.map((sprint, index) => {
            const plannedHeight = (sprint.plannedPoints / maxValue) * 100;
            const completedHeight = (sprint.completedPoints / maxValue) * 100;

            return (
              <div key={sprint.sprintId || index} className="flex-1 flex flex-col items-center">
                <div className="flex-1 w-full flex items-end justify-center gap-1">
                  {/* Planned bar */}
                  <div
                    className="w-6 bg-primary-500 rounded-t transition-all duration-500"
                    style={{ height: `${plannedHeight}%` }}
                    title={`${t('analytics.planned')}: ${sprint.plannedPoints}`}
                  />
                  {/* Completed bar */}
                  <div
                    className="w-6 bg-green-500 rounded-t transition-all duration-500"
                    style={{ height: `${completedHeight}%` }}
                    title={`${t('analytics.completed')}: ${sprint.completedPoints}`}
                  />
                </div>
                {/* Sprint label */}
                <div className="mt-2 text-xs text-neutral-500 text-center truncate w-full">
                  {sprint.sprintName}
                </div>
              </div>
            );
          })}
        </div>

        {/* Average line */}
        {averageVelocity > 0 && (
          <div
            className="absolute left-14 right-0 border-t-2 border-dashed border-orange-400"
            style={{ bottom: `${(averageVelocity / maxValue) * 100}%` }}
          >
            <span className="absolute right-0 -top-5 text-xs text-orange-500 font-medium">
              {t('analytics.average')}: {averageVelocity.toFixed(1)}
            </span>
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="mt-6 pt-4 border-t border-neutral-200 dark:border-neutral-700 grid grid-cols-3 gap-4">
        <div className="text-center">
          <div className="text-2xl font-bold text-neutral-900 dark:text-white">
            {averageVelocity.toFixed(1)}
          </div>
          <div className="text-xs text-neutral-500">{t('analytics.avgVelocity')}</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-neutral-900 dark:text-white">
            {data.length > 0 ? (data[data.length - 1]?.velocity.toFixed(1) ?? '-') : '-'}
          </div>
          <div className="text-xs text-neutral-500">{t('analytics.lastSprint')}</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-neutral-900 dark:text-white">
            {data.length}
          </div>
          <div className="text-xs text-neutral-500">{t('analytics.sprints')}</div>
        </div>
      </div>
    </div>
  );
}
